import { writeDB, api } from './store';
import { Company } from './company';
import { FileRepresenting, Image } from './asset';
import { history } from './history';
import { productPath, productsPath } from '../routes';
import { Category } from './category';
import { EventStatus, TicketLine, PersonalizationRequirement, MetaSettings } from './event';
import { TempImage } from '../pages/Products/components/LogoUploader';
import { OptionType, GetParams, PaginatedLoaderResponse, paginatedLoaderResponseFrom } from './data';
import { oneLevelDiff } from '../util/util';
import { cloneDeep, intersection } from 'lodash';
import { VATRatePerCountry } from './price';
import { apollo } from './graphql';
import {
  PRODUCT_TAX_RATE,
  SET_CART_LIMIT,
  SET_IMMEDIATE_PAYMENT,
  COMBINED_PRODUCTS,
  SET_PRODUCT_SHIPPING_OPTIONS,
} from './queries/product';
import { PaginatedResponse } from './api';
import { Ownership } from './ownerships';
import { FreeShippingMode, ShippingRequirement } from '../generated/graphql';

/* INTERFACES */

export interface MerchSalesStats {
  available: number;
  blocked: number;
  reserved: number;
  returned?: number;
  cart: number;
  sold: number;
  soldOnline: number;
  soldHard: number;
  personalized: number;
}

export interface BasePrice {
  id: string;
  active: boolean;
  activeFrom: Date | null;
  activeUntil: Date | null;
  amount: number;
  originalPrice?: number;
  comment?: string;
  countryCode: string;
  shopId: string | null;
  $permissions: Array<'update' | 'destroy'>;
}

export interface PriceCreate extends BasePrice {
  productTaxesPerCountry?: VATRatePerCountry;
}

export interface PriceUpdate {
  id: string;
  shopId?: string;
  activeFrom?: Date;
  activeUntil?: Date;
  amount?: number;
  originalPrice?: number;
  overlayDetailImage?: FileRepresenting;
  overlayListingImage?: FileRepresenting;
  comment?: string;
}

export interface Price extends BasePrice {
  overlayDetailImage?: Image;
  overlayListingImage?: Image;
}

export interface Tax {
  amount?: number;
  countryCode?: string;
  countryGroup?: string;
  createdAt?: string;
  id?: string;
  productVariantId?: string;
  updatedAt?: string;
}

export interface ProductVariantBase {
  id: string;
  legacyId?: string | null;
  active: boolean;
  activeFrom: Date;
  activeUntil: Date;
  availableCount: number;
  cartCount: number;
  description: string | null;
  hasPriceInFuture: boolean;
  minimumStockCount: number;
  order: number;
  prices: Price[];
  reservedCount: number;
  stockCount: number;
  taxes: Tax[];
  weight: number | null;
  name?: string;
  fulfillmentCount?: string;
  sku?: string;
  ean?: string | null;
  _delete?: boolean;
}
export interface ProductVariant extends ProductVariantBase {
  launchSaleAt?: string;
  physical?: boolean;
  categoryAssignments?: {
    category: Category;
    order: number;
  };
}
export interface ProductVariantEdit extends ProductVariantBase {
  launchSaleAt?: Date;
}

export interface ProductCategory {
  id: string;
  name: string;
  order: number;
  category: Category;
  shopId: string;
  shopName: string;
  urlInShop: string;
}

export interface StatData {
  total: number;
  online: number;
}

export interface DateStatData {
  date: Date;
  total: number;
  online: number;
}

export interface SalesStat {
  name?: string;
  id: string;
  order?: number;
  lifetime: StatData;
  lastYear: StatData;
  lastThirtyDays: StatData;
  lastSixMonths: StatData;
  today: StatData;
  hour: StatData;
  month0: DateStatData;
  month1: DateStatData;
  month2: DateStatData;
  month3: DateStatData;
  month4: DateStatData;
  month5: DateStatData;
}

interface CombinedProduct {
  id: string;
  artist?: string;
  name?: string;
  sku?: string;
  __typename: 'MerchProduct' | 'Event' | 'DiscountProduct' | 'ShippingProduct';
}

export interface ProductBase {
  legacyId?: string;
  sku?: string;
  slug?: string | null;
  artist?: string | null;
  artistDe?: string | null;
  artistEn?: string | null;
  color?: string | null;
  colorDe?: string | null;
  colorEn?: string | null;
  material?: string | null;
  materialDe?: string | null;
  materialEn?: string | null;
  categories?: ProductCategory[];
  description?: string | null;
  descriptionDe?: string | null;
  descriptionEn?: string | null;
  details?: TicketLine;
  name?: string | null;
  nameDe?: string | null;
  nameEn?: string | null;
  owningCompanyId?: string;
  personalizationRequirement?: PersonalizationRequirement | null;
  productSafetyDescription?: string | null;
  productSafetyDescriptionDe?: string | null;
  productSafetyDescriptionEn?: string | null;
  productType?: string | null;
  productTypeDe?: string | null;
  productTypeEn?: string | null;
  publishedFrom?: Date;
  publishedUntil?: Date;
  vendorIds?: string[];
  motive?: string;
  status?: EventStatus;
  salesStats?: Array<SalesStat>;
  productVariants?: MerchProductVariant[];
  price?: string;
  stock?: string;
  available?: string;
  reserved?: string;
  cart?: string;
  fulfillment?: string;
  incomplete?: string;
  additionalInformation?: TicketLine;
  costAccountOne?: string;
  costAccountTwo?: string;
  recordLabel?: string;
  recordDistributor?: string;
  recordReleasedAt?: Date;
  recordType?: string;
  descriptiveName?: string;
  shippableFrom?: Date;
  dhlShippingFrom?: Date | null;
  publicShippableFrom?: Date;
  taxesPerCountry: VATRatePerCountry;
  cartLimit?: number;
  immediatePaymentAtStockQuota: number | null;
  meta: MetaSettings;
  combinedProductIds?: string[][] | null;
  combinedProducts?: Array<CombinedProduct[]> | null;
  excludeFromFeeds?: boolean;
  googleFeedCategory?: string | null;
  googleFeedCategoryOptions: string[];
  shippingRequirements?: ShippingRequirement[];
  freeShippingMode?: 'OFF' | 'UNANIMOUS' | 'FORCE';
  prohibitedShippingTags?: string[];
}

export interface Product extends ProductBase {
  id: string;
  owningCompany?: Company;
  vendors?: Company[];
  galleryImages?: Image[];
  images?: Image[];
  itemType: OptionType;
  $permissions: Array<'update' | 'destroy' | 'duplicate' | 'change_on_sale' | 'update_chart_overrides'>;
  __typename: 'MerchProduct' | 'Event' | 'DiscountProduct' | 'ShippingProduct';
  contractId?: string;
  customsTariffNumber?: string;
  ownerships?: Ownership[];
}

export interface ProductPatch extends ProductBase {
  id?: string;
  $permissions?: Array<'update' | 'destroy'>;
}

export interface GenericProduct {
  id: string;
  slug: string;
  type: 'product' | 'event' | 'refund' | 'shipping' | 'discount' | 'auto_cancelled';
  name: string;
  nameDe: string;
  nameEn: string;
  sku: string;
}

export interface MerchProductVariant {
  id: string;
  legacyId?: string;
  private: boolean;
  sku: string;
  description: string;
  reservedCount?: number;
  cartCount?: number;
  fulfillmentCount?: number;
  incompleteCount?: number;
  pendingDeliveryCount?: number;
  availableCount?: number;
  activeFrom?: Date | null;
  activeUntil?: Date | null;
  stockCount?: number;
  minimumStockCount?: number;
  weight?: number;
  ean?: string;
  order?: number;
  priceInFuture?: boolean;
  active?: boolean;
  prices: Price[];
  taxRate?: number;
  $permissions: string[];
  taxes?: Array<{
    id?: string;
    countryCode?: string;
    amount?: number;
  }>;
  images?: Image[];
  price?: number;
  netPrice?: number;
  warehouseLocations?: Array<{
    id?: string;
    name?: string;
    tags?: string[];
  }>;
}

export type ProductInGroup = Partial<Product> & { groupId: string };

/* PRODUCT CREATE */
export async function addProduct(product: Partial<ProductPatch>): Promise<void> {
  const response = await api.createProduct(product);
  const responseProduct = response.data as Product;
  writeDB.table('products').insert(responseProduct);
  if (responseProduct.slug) {
    history.push(productPath(responseProduct.slug));
  }
}

export async function addProductImage(productId: string, file: string, order: number, tags: string[]): Promise<void> {
  const product = writeDB.table('products').find(productId);
  if (product) {
    const response = (
      await api.createImage({
        productId,
        file,
        order,
        tags,
      })
    ).data;
    const newImage: Image = { ...response, order };
    writeDB.table('products').update(productId, {
      images: [...(product.images ? product.images : []), newImage].sort((a, b) => (a.order || 0) - (b.order || 0)),
    });
  }
}

export async function addProductPrice(productId: string, price: Partial<PriceCreate>): Promise<void> {
  await api.createPrice({
    productId,
    ...price,
  });
}

export async function changeProductVat(productId: string, price: Partial<PriceCreate>): Promise<void> {
  await api.createPrice({
    productId,
    ...price,
    productTaxesPerCountry: {
      ...price.productTaxesPerCountry,
      de: parseFloat(`${price.productTaxesPerCountry?.de}`),
    },
  });
}

export async function duplicateProduct(productId: string): Promise<void> {
  const response = await api.duplicateProduct({ productId });
  const updated = await api.updateProduct({
    productId: response.data.id,
    nameDe: response.data.nameDe + ' - copy',
  });
  writeDB.table('products').insert(updated.data);
  history.push(productPath(updated.data as Product));
}

/* PRODUCT READ */
export async function getRawProducts({ page, perPage, term }: GetParams): Promise<PaginatedResponse<Product[]>> {
  const response = await api.indexProducts({
    page: (page || 0) + 1,
    perPage,
    term,
  });
  return response as PaginatedResponse<Product[]>;
}

export async function getProducts({
  term,
  page,
  perPage,
  status,
  sortBy = 'artist',
  sortDir = 'desc',
  lowAvailability,
}: {
  term?: string;
  page?: number;
  perPage?: number;
  status?: string[];
  sortBy?: string;
  sortDir?: string;
  lowAvailability?: boolean;
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexProducts({
    page: (page || 0) + 1,
    perPage,
    term,
    status,
    sortDir,
    sortBy,
    lowAvailability,
  });
  writeDB.table('products').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getRawProduct(productId: string): Promise<PaginatedResponse<Product>> {
  const response = await api.showProduct({ productId });
  return response as PaginatedResponse<Product>;
}

export const SHIPPING_REQUIREMENTS: ShippingRequirement[] = ['over_18'];

export async function getProduct(productId: string): Promise<Product> {
  const product = (await api.showProduct({ productId })).data as Product;
  product.taxesPerCountry = Object.fromEntries(
    Object.entries(product.taxesPerCountry as VATRatePerCountry).sort((a, b) => a[0].localeCompare(b[0]))
  ) as VATRatePerCountry;
  writeDB.table('products').upsert(product);
  return product;
}

export async function showGeneralProduct(productId: string): Promise<GenericProduct> {
  const generalProduct = (await api.showGeneralProduct({ productId })).data;
  return generalProduct as GenericProduct;
}

export function getProductName(product?: Partial<Product>): string {
  if (!product) {
    return '';
  }

  return [product.artist, product.productType, product.name, product.color].filter(Boolean).join(' - ');
}

export async function getRawAdditionalProducts({
  page,
  perPage,
  term,
  type,
}: GetParams): Promise<PaginatedResponse<Product[]>> {
  const response = await api.indexAdditionalProducts({
    page: (page || 0) + 1,
    perPage,
    term,
    type,
  });
  return response as PaginatedResponse<Product[]>;
}

export async function getCombinedProducts(productId: string): Promise<void> {
  const response = await apollo.query({
    query: COMBINED_PRODUCTS,
    variables: { productId },
    fetchPolicy: 'network-only',
  });

  if (!response.data.product) return;
  writeDB.table('products').update(productId, response.data.product as Product);
}

/* PRODUCT UPDATE */
export async function patchProduct(productId: string, product: Partial<Product>, redirect = true): Promise<void> {
  const response = await api.updateProduct({
    productId,
    ...product,
    slug: product.slug ? product.slug.trim() : undefined,
  });
  // TODO: Update api model to match local interface
  const data = response.data as unknown as Product;
  writeDB.table('products').update(productId, data);
  if (response.data.slug && redirect) {
    history.push(productPath(response.data.slug));
  }
}

export async function patchProductImage(
  productId: string,
  imageId: string,
  order: number,
  tags: string[]
): Promise<void> {
  const product = writeDB.table('products').find(productId);
  if (product) {
    const response = (
      await api.updateImage({
        imageId,
        order,
        tags,
      })
    ).data;
    const oldImages = [...(product.images ? product.images : [])];
    const editedItemIndex = oldImages.findIndex((item) => item.id === imageId);
    oldImages[editedItemIndex] = response;
    oldImages.sort((a, b) => (a.order || 0) - (b.order || 0));
    writeDB.table('products').update(productId, { images: oldImages });
  }
}

export async function ensureProducts(ids: string[]): Promise<void> {
  const productsTable = writeDB.table('products');
  const idsToRequest = ids.filter((e) => !productsTable.find(e));
  const products = await Promise.all(
    idsToRequest.map(async (productId) => (await api.showProduct({ productId })).data)
  );
  productsTable.upsert(products);
}

export async function persistImageChanges(productId: string, newImages: Array<Image | TempImage>): Promise<void> {
  const product = writeDB.table('products').find(productId);
  const orderedImages = newImages.map((img, index) => {
    return {
      ...img,
      order: index,
    };
  });

  if (product) {
    // eslint-disable-next-line no-prototype-builtins
    const toAdd = orderedImages.filter((i) => i.hasOwnProperty('encodedString'));
    // eslint-disable-next-line no-prototype-builtins
    const oldImages = orderedImages.filter((i) => i.hasOwnProperty('url'));
    const persistanceRequests: Array<Promise<{ data: Image } | void>> = [];

    toAdd.forEach((ni) => {
      const img = ni as TempImage;
      persistanceRequests.push(
        api.createImage({
          productId,
          file: img.encodedString,
          order: img.order,
          tags: img.tags,
        })
      );
    });

    const diff = oneLevelDiff(product.images || [], oldImages as Image[]);

    diff.deleted.forEach((imageId) => {
      persistanceRequests.push(
        api.destroyImage({
          imageId,
        })
      );
    });

    diff.edited.forEach((image) => {
      persistanceRequests.push(
        api.updateImage({
          imageId: image.id,
          order: image.order,
          tags: image.tags,
        })
      );
    });

    const response = await Promise.all(persistanceRequests);

    const updatedImages = response.filter((i) => i).map((i) => (i as { data: Image }).data);

    const addedImages = response
      .map((img) => (img ? img.data : undefined))
      .filter(Boolean)
      .filter((img) => {
        if (product.images) {
          return !product.images.find((i) => {
            return img && i.id === img.id;
          });
        }
        return true;
      });

    const editedProductImages = [...(product.images ? product.images : [])]
      .filter((img) => !diff.deleted.includes(img.id))
      .map((img) => {
        const didUpdate = updatedImages.find((i) => img.id === i.id);
        return didUpdate ? didUpdate : img;
      })
      .sort((a, b) => {
        if (a && a.order && b && b.order) {
          return a.order - b.order;
        }
        return 0;
      });

    const newProductImages = [...editedProductImages, ...addedImages].sort((a, b) => {
      return (a?.order || 0) - (b?.order || 0);
    });

    writeDB.table('products').update(productId, {
      images: newProductImages as Image[],
    });
  }
}

export async function updateProductPrice(priceId: string, price: Partial<PriceUpdate>): Promise<void> {
  try {
    await api.updatePrice({ priceId, ...price });
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((error as any)['response']?.['data']?.['status'] === 422) {
      throw error;
    }
    throw ensureProducts;
  }
}

export async function setCartLimit(variables: {
  cartLimit: number;
  productId: string;
  type: 'event' | 'product';
}): Promise<void> {
  const response = await apollo.mutate({
    mutation: SET_CART_LIMIT,
    variables,
  });

  const data = response.data?.setCartLimit?.product;

  if (variables.type === 'event') {
    writeDB.table('events').update(variables.productId, { ...data });
  }
  if (variables.type === 'product') {
    writeDB.table('products').update(variables.productId, { cartLimit: data?.cartLimit });
  }
}

export async function setShippingOptions({
  productId,
  productType,
  shippingRequirements,
  prohibitedShippingTags,
  freeShippingMode,
}: {
  productId: string;
  productType: 'event' | 'product';
  shippingRequirements?: ShippingRequirement[];
  prohibitedShippingTags?: string[];
  freeShippingMode?: FreeShippingMode;
}): Promise<void> {
  const response = await apollo.mutate({
    mutation: SET_PRODUCT_SHIPPING_OPTIONS,
    variables: {
      productId,
      shippingRequirements: intersection(shippingRequirements, SHIPPING_REQUIREMENTS),
      prohibitedShippingTags: prohibitedShippingTags ?? null,
      freeShippingMode: freeShippingMode ?? null,
    },
  });
  writeDB.table(productType === 'product' ? 'products' : 'events').update(productId, {
    shippingRequirements: response.data?.setProductShippingOptions?.product?.shippingRequirements,
    prohibitedShippingTags: response.data?.setProductShippingOptions?.product?.prohibitedShippingTags,
    freeShippingMode: response.data?.setProductShippingOptions?.product?.freeShippingMode,
  });
}

export async function setImmediatePaymentRate(variables: {
  immediatePaymentAtStockQuota: number;
  productId: string;
  type: 'event' | 'product';
}): Promise<void> {
  const response = await apollo.mutate({
    mutation: SET_IMMEDIATE_PAYMENT,
    variables,
  });

  const data = response.data?.setImmediatePaymentAtStockQuota?.product;

  if (variables.type === 'event') {
    writeDB.table('events').update(variables.productId, { ...data });
  }
  if (variables.type === 'product') {
    writeDB
      .table('products')
      .update(variables.productId, { immediatePaymentAtStockQuota: data?.immediatePaymentAtStockQuota });
  }
}

/* PRODUCT DELETE */
export async function deleteProductPrice(priceId: string): Promise<void> {
  await api.destroyPrice({ priceId });
}

export async function deleteProduct(productId: string): Promise<void> {
  await api.destroyProduct({ productId });
  history.push(productsPath());
}

/* PRODUCT VARIANT CREATE */
export async function createVariant({
  productId,
  changes,
}: {
  productId: string;
  changes: Partial<MerchProductVariant>;
}): Promise<void> {
  await api.createProductVariant({
    productId,
    ...changes,
  });
  await getProduct(productId);
}

/* PRODUCT VARIANT READ */
export async function searchAdditionalProductByVariantId(
  id: string,
  page = 1
): Promise<PaginatedResponse<{ id: string; name?: string }>> {
  const resp = await api.indexAdditionalProducts({
    page,
    perPage: 100,
  });
  const results = resp.data as Product[];
  let name: string | undefined;

  results.forEach((item) => {
    if (item.productVariants) {
      const exists = item.productVariants.find((variant) => variant.id === id);
      if (exists) {
        // If the item does not have name, sku or description we return id to make
        // sure we don't search the next page
        name = [item.name, exists.sku, exists.description].filter(Boolean).join(' - ') || id;
      }
    }
  });

  // If it is not found in this page, search in the next page
  if (!name && page < resp.totalPages) {
    const resp2 = await searchAdditionalProductByVariantId(id, page + 1);
    name = resp2.data.name;
  }

  return {
    data: {
      id,
      name,
    },
    page,
    perPage: resp.perPage,
    totalCount: resp.totalCount,
    totalPages: resp.totalPages,
    permissions: resp.permissions,
  };
}

/* PRODUCT VARIANT UPDATE */
export async function updateVariants(variants: MerchProductVariant[]): Promise<void> {
  const apiPromises: Array<
    Promise<{
      data: Partial<MerchProductVariant>;
    }>
  > = [];
  variants.forEach((item) => {
    // TODO: Update api model to match local interface
    // eslint-disable-next-line
    // @ts-ignore
    apiPromises.push(api.updateProductVariant({ productVariantId: item.id, ...item }));
  });
  await Promise.all(apiPromises);
}

export async function updateVariant({
  productId,
  productVariantId,
  changes,
}: {
  productId: string;
  productVariantId: string;
  changes: Partial<MerchProductVariant>;
}): Promise<void> {
  await api.updateProductVariant({
    productVariantId,
    ...changes,
  });
  await getProduct(productId);
}

/* PRODUCT VARIANT DELETE */
export async function deleteVariant(variantId: string, productId: string): Promise<void> {
  await api.destroyProductVariant({ productVariantId: variantId });
  await getProduct(productId);
}

/* PRODUCT VARIANT METHODS */
export function getProductVariantPrice(variant: MerchProductVariant): number | undefined {
  const activePrice = variant.netPrice ?? variant.prices?.filter((p) => p.active).map((p) => p.amount);
  if (typeof activePrice === 'number') {
    return activePrice;
  }
  if (activePrice?.length) {
    return Math.min(...activePrice);
  }
  return undefined;
}

function normalizeMonths(months?: Array<DateStatData>): Partial<MerchSalesStats> {
  if (!months) {
    return {};
  }
  const normalizedMonth: Record<string, DateStatData> = {};

  months.forEach((month, index) => {
    normalizedMonth[`month${index}`] = {
      ...month,
    };
  });
  return normalizedMonth;
}

function getSalesStatAggregate(salesStats: Array<SalesStat>): SalesStat {
  if (salesStats.length === 1) {
    return { ...salesStats[0], ...{ name: 'aggr', id: '' } };
  } else {
    return salesStats.reduce((previous, current) => {
      const accumulatedSum = {
        id: '',
        name: 'aggr',
      };

      Object.keys(previous).forEach((key) => {
        if (key !== 'id' && key !== 'name' && key !== 'order') {
          accumulatedSum[key] = {
            online: previous[key].online + current[key].online,
            total: previous[key].total + current[key].total,
          };
        }
      });

      return accumulatedSum as SalesStat;
    });
  }
}

export async function getProductSaleStats(productId: string): Promise<void> {
  const product = writeDB.table('products').find(productId);
  if (product) {
    const result = await api.showProductSalesStats({ productId });
    const normalizedSalesStats: Array<SalesStat> = [];

    result.data.stats?.forEach((stat) => {
      const tempStat = cloneDeep(stat);
      // OAS says everything is optional, it's not. KS-612
      const sixMonthSum = (
        tempStat.months as Array<{
          date: Date;
          online: number;
          total: number;
        }>
      ).reduce((acc, current) => {
        return {
          date: acc.date,
          online: acc.online + current.online,
          total: acc.total + current.total,
        };
      });
      delete tempStat.months;

      const variant = product.productVariants?.find((variant) => variant.description === stat.name);

      const normalizedStat = {
        ...tempStat,
        order: variant?.order,
        lastSixMonths: {
          online: sixMonthSum.online / 6,
          total: sixMonthSum.total / 6,
        },
        ...normalizeMonths(stat.months?.reverse() as DateStatData[]),
      } as SalesStat;

      normalizedSalesStats.push(normalizedStat);
    });
    if (normalizedSalesStats.length > 1) {
      normalizedSalesStats.push(getSalesStatAggregate(normalizedSalesStats));
    }
    writeDB.table('products').update(productId, { salesStats: normalizedSalesStats });
  }
}

export async function getProductTaxDetails({
  productId,
  productVariantId,
  countryCode,
}: {
  productId: string;
  productVariantId: string;
  countryCode: string;
}): Promise<number | undefined> {
  const result = await apollo.query({
    query: PRODUCT_TAX_RATE,
    variables: { productId, productVariantId, countryCode },
    fetchPolicy: 'network-only',
  });

  return result.data?.product?.productVariant?.taxRate;
}
